import { useEffect, useRef, useState } from 'react';

/**
 * Adds an `is-visible` state once the element scrolls into view.
 * Pairs with the `.reveal` / `.reveal.is-visible` CSS classes.
 *
 * Hardened so content never gets stuck hidden: reveals immediately if the
 * element is already on-screen at mount, and has a safety fallback in case
 * IntersectionObserver never fires.
 */
export function useReveal({ threshold = 0.15, rootMargin = '0px 0px -8% 0px' } = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Already visible on first paint (e.g. above-the-fold) -> reveal now.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setVisible(true);
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold, rootMargin },
    );

    observer.observe(el);

    // Safety net: never leave content hidden if the observer is throttled.
    const fallback = setTimeout(() => setVisible(true), 2500);

    return () => {
      observer.disconnect();
      clearTimeout(fallback);
    };
  }, [threshold, rootMargin]);

  return { ref, visible };
}
